import React, { useEffect, useRef, useCallback } from 'react';
import {
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  type TextFieldProps,
} from '@mui/material';
import { Mic, MicOff } from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import { useSpeechToText } from '../hooks/useSpeechToText';

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.15); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`;

export interface DictationTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onTranscriptChange?: (text: string) => void;
}

export default function DictationTextField({
  value = '',
  onChange,
  onTranscriptChange,
  ...textFieldProps
}: DictationTextFieldProps) {
  const {
    isListening,
    transcript,
    interimTranscript,
    isSupported,
    error,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechToText();

  const lastTranscriptLengthRef = useRef(0);

  // When new final transcript text arrives, append it to the current value
  useEffect(() => {
    if (transcript.length > lastTranscriptLengthRef.current) {
      const newText = transcript.slice(lastTranscriptLengthRef.current);
      const currentValue = typeof value === 'string' ? value : '';
      const separator = currentValue.length > 0 && !currentValue.endsWith(' ') ? ' ' : '';
      const updatedValue = currentValue + separator + newText;

      onTranscriptChange?.(updatedValue);

      // Synthesize a change event for the onChange handler
      if (onChange) {
        const syntheticEvent = {
          target: { value: updatedValue },
          currentTarget: { value: updatedValue },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    }
    lastTranscriptLengthRef.current = transcript.length;
  }, [transcript, value, onChange, onTranscriptChange]);

  // Reset transcript tracking when the component's value is cleared externally
  useEffect(() => {
    if (value === '' || value === undefined) {
      lastTranscriptLengthRef.current = 0;
      resetTranscript();
    }
  }, [value, resetTranscript]);

  const handleMicClick = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // Compose the display value: current value + interim transcript preview
  const displayValue = isListening && interimTranscript
    ? `${value}${value && !String(value).endsWith(' ') ? ' ' : ''}${interimTranscript}`
    : value;

  const micButton = (
    <IconButton
      onClick={handleMicClick}
      disabled={!isSupported}
      edge="end"
      aria-label={isListening ? 'Stop dictation' : 'Start dictation'}
      size="small"
      sx={{
        color: isListening ? 'primary.main' : 'text.secondary',
        ...(isListening && {
          animation: `${pulse} 1.4s ease-in-out infinite`,
        }),
      }}
    >
      {isListening ? <Mic /> : <MicOff />}
    </IconButton>
  );

  return (
    <TextField
      {...textFieldProps}
      value={displayValue}
      onChange={onChange}
      error={textFieldProps.error || !!error}
      helperText={error || textFieldProps.helperText}
      slotProps={{
        ...textFieldProps.slotProps,
        input: {
          ...((textFieldProps.slotProps as any)?.input || {}),
          endAdornment: (
            <InputAdornment position="end">
              {!isSupported ? (
                <Tooltip title="Speech recognition is not supported in this browser">
                  <span>{micButton}</span>
                </Tooltip>
              ) : (
                <Tooltip title={isListening ? 'Stop dictation' : 'Start dictation'}>
                  {micButton}
                </Tooltip>
              )}
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
